import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { vehicles, categories } from "@/mocks/vehicles";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("tipo") || "all";

  const [activeCategory, setActiveCategory] = useState(initialType);
  const [priceRange, setPriceRange] = useState(500);
  const [transmission, setTransmission] = useState("all");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [sortBy, setSortBy] = useState("price_asc");

  const filtered = vehicles
    .filter((v) => activeCategory === "all" || v.category === activeCategory)
    .filter((v) => v.price <= priceRange)
    .filter((v) => transmission === "all" || v.transmission === transmission)
    .filter((v) => !onlyAvailable || v.available)
    .sort((a, b) => {
      if (sortBy === "price_asc") return a.price - b.price;
      if (sortBy === "price_desc") return b.price - a.price;
      if (sortBy === "rating") return b.rating - a.rating;
      return 0;
    });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20 max-w-7xl mx-auto px-4 md:px-8 py-10">
        <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Busca Avançada</h1>
        <p className="text-gray-500 text-sm mb-8">Campinas e região — {filtered.length} veículos encontrados</p>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="bg-gray-50 rounded-2xl p-6 sticky top-24">
              <h2 className="font-bold text-gray-900 text-base mb-5">Filtrar por</h2>

              {/* Category */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Tipo de Veículo</p>
                <div className="flex flex-col gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer text-left ${
                        activeCategory === cat.id
                          ? "bg-orange-500 text-white"
                          : "bg-white text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Preço máximo: <span className="text-orange-500">R$ {priceRange}/dia</span>
                </p>
                <input
                  type="range"
                  min={50}
                  max={500}
                  step={10}
                  value={priceRange}
                  onChange={(e) => setPriceRange(Number(e.target.value))}
                  className="w-full accent-orange-500 cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>R$ 50</span>
                  <span>R$ 500</span>
                </div>
              </div>

              {/* Transmission */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Câmbio</p>
                <div className="flex gap-2">
                  {["all", "Manual", "Automático"].map((t) => (
                    <button
                      key={t}
                      onClick={() => setTransmission(t)}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        transmission === t
                          ? "bg-orange-500 text-white"
                          : "bg-white text-gray-600 hover:bg-orange-50"
                      }`}
                    >
                      {t === "all" ? "Todos" : t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={onlyAvailable}
                    onChange={(e) => setOnlyAvailable(e.target.checked)}
                    className="w-4 h-4 accent-orange-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 font-medium">Apenas disponíveis</span>
                </label>
              </div>

              <button
                onClick={() => { setActiveCategory("all"); setPriceRange(500); setTransmission("all"); setOnlyAvailable(false); }}
                className="w-full text-orange-500 text-sm font-semibold py-2 border border-orange-200 rounded-xl hover:bg-orange-50 transition-all cursor-pointer whitespace-nowrap"
              >
                Limpar Filtros
              </button>
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm text-gray-500">{filtered.length} veículos encontrados</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gray-200 rounded-xl px-3 py-2 text-gray-700 bg-white focus:outline-none focus:border-orange-400 cursor-pointer"
              >
                <option value="price_asc">Menor preço</option>
                <option value="price_desc">Maior preço</option>
                <option value="rating">Melhor avaliação</option>
              </select>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-car-line text-5xl"></i>
                </div>
                <p className="text-lg font-medium">Nenhum veículo encontrado</p>
                <p className="text-sm mt-1">Tente ajustar os filtros</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((vehicle) => (
                  <Link
                    key={vehicle.id}
                    to={`/veiculo/${vehicle.id}`}
                    className="bg-white border border-gray-100 rounded-2xl overflow-hidden group hover:border-orange-200 hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden bg-gray-50">
                      <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full ${vehicle.available ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                        {vehicle.available ? "Disponível" : "Indisponível"}
                      </div>
                      {vehicle.discount > 0 && (
                        <div className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">-{vehicle.discount}%</div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm">{vehicle.name}</h3>
                          <span className="text-xs text-orange-500 font-medium">{vehicle.category}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-yellow-400 text-xs"></i>
                          </div>
                          {vehicle.rating}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1"><i className="ri-user-line"></i>{vehicle.seats} lug.</span>
                        <span className="flex items-center gap-1"><i className="ri-settings-3-line"></i>{vehicle.transmission}</span>
                        <span className="flex items-center gap-1"><i className="ri-gas-station-line"></i>{vehicle.fuel}</span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          {vehicle.originalPrice > vehicle.price && (
                            <span className="text-gray-400 text-xs line-through block">R$ {vehicle.originalPrice}/dia</span>
                          )}
                          <span className="text-orange-500 font-extrabold text-xl">R$ {vehicle.price}<span className="text-xs font-normal text-gray-400">/dia</span></span>
                        </div>
                        <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-xl whitespace-nowrap">Reservar</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
